import type { ReactNode } from 'react'
import cx from 'classnames'
import { Icon } from '@/components/Icon'
import { Skeleton, useSkeletonListContext } from '@/components/Skeleton'
import { Tooltip } from '@/components/Tooltip'
import styles from './PreviewQuestions.module.css'

export interface PreviewQuestionsProps {
  children?: ReactNode
  /**
   * Shown when there are no questions.
   */
  emptyText?: string
  isEmpty: boolean
}

/**
 * The questions of a group of sample inputs, one per row.
 */
export function PreviewQuestions({
  children,
  emptyText,
  isEmpty,
}: PreviewQuestionsProps) {
  if (isEmpty) {
    return emptyText ? <p className={styles.empty}>{emptyText}</p> : null
  }
  return <div className={styles.root}>{children}</div>
}

export interface PreviewQuestionRowProps {
  title: string
  description?: string
  errors?: readonly string[]
  /**
   * Keeps the answer editor at its own width, like a switch, instead of
   * filling the row's half.
   */
  isCompactEditor?: boolean
  /**
   * The answer editor.
   */
  children?: ReactNode
}

/**
 * A question, with its answer editor beside it.
 */
export function PreviewQuestionRow({
  title,
  description,
  errors = [],
  isCompactEditor,
  children,
}: PreviewQuestionRowProps) {
  return (
    <div className={styles.row}>
      <div className={styles.label}>
        <span className={styles.title}>{title}</span>
        {description && (
          <Tooltip content={description} className={styles.description}>
            <span className={styles.info}>
              <Icon name="info" size={14} aria-label={description} />
            </span>
          </Tooltip>
        )}
      </div>
      <div className={cx(styles.editor, isCompactEditor && styles.compact)}>
        {children}
      </div>
      {errors.length > 0 && (
        <div className={styles.errors}>{errors.join(' / ')}</div>
      )}
    </div>
  )
}

/**
 * A skeleton for a {@link PreviewQuestionRow}.
 */
export function PreviewQuestionRowSkeleton() {
  const { vary } = useSkeletonListContext()
  return (
    <div className={styles.row}>
      <div className={styles.label}>
        <span className={styles.title}>
          <Skeleton width={vary(120)} />
        </span>
      </div>
      <div className={styles.editor}>
        <Skeleton variant="rect" height="var(--control)" block />
      </div>
    </div>
  )
}

export interface PreviewQuestionHeadingProps {
  title: string
}

/**
 * A question that takes no answer, like a breaker.
 */
export function PreviewQuestionHeading({ title }: PreviewQuestionHeadingProps) {
  return <div className={styles.heading}>{title}</div>
}
